import { Loader } from "@googlemaps/js-api-loader";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

const LOADER_OPTIONS = {
    apiKey: "AIzaSyAm5ycUJH0_N4Gd0-9P0MwnExXXTTgL0vE",
    version: "weekly",
    libraries: ["marker"],
};

const BASE_MAP_OPTIONS = {
    center: { lat: 0, lng: 0 },
    zoom: 4,
    mapId: "7e1c4c732de8a407",
    mapTypeControl: false,
    streetViewControl: false,
    zoomControl: true,
};

let librariesPromise;

const ensureLibraries = () => {
    if (!librariesPromise) {
        const loader = new Loader(LOADER_OPTIONS);
        librariesPromise = Promise.all([loader.importLibrary("maps"), loader.importLibrary("marker")]);
    }

    return librariesPromise;
};

const createDealerMarkerContent = () => {
    const el = document.createElement("div");
    el.className = "map-marker";
    return el;
};

const createRealizationMarkerContent = () => {
    const wrapper = document.createElement("div");
    const img = document.createElement("img");
    img.src = "/assets/icons/pin.svg";
    img.alt = "";
    img.style.width = "32px";
    img.style.height = "auto";
    wrapper.appendChild(img);
    return wrapper;
};

const createClusterMarkerElement = (count) => {
    const el = document.createElement("div");
    el.className = "h-2 w-2 rounded-full text-white bg-prim flex items-center justify-center";
    el.textContent = String(count);
    el.setAttribute("aria-hidden", "true");
    return el;
};

const getMarkerMeta = (el) => ({
    name: el.dataset.name ?? "",
    address: el.dataset.address ?? "",
    postalcode: el.dataset.postalcode ?? "",
    city: el.dataset.city ?? "",
    country: el.dataset.countryName ?? el.dataset.country ?? "",
    products: el.dataset.productsLabel ?? el.dataset.products ?? "",
});

const buildInfoElement = ({ name, address, postalcode, city, country, products }) => {
    const wrapper = document.createElement("div");
    wrapper.className = "map-info text-sm space-y-0.25";

    if (name) {
        const title = document.createElement("div");
        title.className = "font-semibold text-base";
        title.textContent = name;
        wrapper.appendChild(title);
    }

    const addressLines = [];
    if (address) addressLines.push(address);
    const locality = [postalcode, city].filter(Boolean).join(" ").trim();
    if (locality) addressLines.push(locality);
    if (country) addressLines.push(country);

    if (addressLines.length) {
        const addressEl = document.createElement("div");
        addressEl.className = "leading-tight";
        addressLines.forEach((line) => {
            const lineEl = document.createElement("div");
            lineEl.textContent = line;
            addressEl.appendChild(lineEl);
        });
        wrapper.appendChild(addressEl);
    }

    if (products) {
        const productsEl = document.createElement("div");
        productsEl.className = "text-xs text-black/70";
        productsEl.textContent = products;
        wrapper.appendChild(productsEl);
    }

    return wrapper.childElementCount ? wrapper : null;
};

const initDealerMap = () => {
    const mapContainer = document.querySelector("#map-dealers");
    if (!mapContainer) {
        return;
    }

    const markerElements = Array.from(document.querySelectorAll(".address-map[data-lat][data-lng]"))
        .filter((el) => !el.hasAttribute("data-realization-marker"));

    ensureLibraries()
        .then(() => {
            const { Map, LatLngBounds } = google.maps;
            const { AdvancedMarkerElement } = google.maps.marker;

            mapContainer.innerHTML = "";
            const map = new Map(mapContainer, { ...BASE_MAP_OPTIONS });
            const dealerInfoWindow = new google.maps.InfoWindow();
            const bounds = new LatLngBounds();
            const markers = [];

            markerElements.forEach((el) => {
                const lat = parseFloat(el.dataset.lat || "");
                const lng = parseFloat(el.dataset.lng || "");
                if (Number.isNaN(lat) || Number.isNaN(lng)) {
                    return;
                }

                bounds.extend({ lat, lng });

                const marker = new AdvancedMarkerElement({
                    position: { lat, lng },
                    map,
                    content: createDealerMarkerContent(),
                });

                const meta = getMarkerMeta(el);
                marker.addListener("click", () => {
                    const infoEl = buildInfoElement(meta);
                    if (infoEl) {
                        dealerInfoWindow.setContent(infoEl);
                        dealerInfoWindow.open({ map, anchor: marker });
                    }
                });

                markers.push(marker);
            });

            if (!bounds.isEmpty()) {
                map.fitBounds(bounds);
                if (map.getZoom() > 14) {
                    map.setZoom(14);
                }
            }

            if (markers.length > 1) {
                new MarkerClusterer({
                    map,
                    markers,
                    minimumClusterSize: 2,
                    renderer: {
                        render: ({ markers: clusterMarkers, position }) => {
                            if (clusterMarkers.length < 2) {
                                return null;
                            }

                            return new AdvancedMarkerElement({
                                position,
                                content: createClusterMarkerElement(clusterMarkers.length),
                                zIndex: clusterMarkers.length,
                            });
                        },
                    },
                });
            }

        })
        .catch((error) => {
            console.error("Error initializing dealer map:", error);
        });
};

const initRealizationsMap = () => {
    const mapContainer = document.querySelector("[data-realizations-map]");
    if (!mapContainer) {
        return;
    }

    const componentRoot = mapContainer.closest("[data-realizations-component]") ?? document;
    const markerElements = Array.from(componentRoot.querySelectorAll("[data-realization-marker]"));

    ensureLibraries()
        .then(() => {
            const { Map, LatLngBounds } = google.maps;
            const { AdvancedMarkerElement } = google.maps.marker;

            // Clear the container before initializing the map
            mapContainer.innerHTML = "";
            const map = new Map(mapContainer, { ...BASE_MAP_OPTIONS });
            const realizationsInfoWindow = new google.maps.InfoWindow();
            const bounds = new LatLngBounds();
            const markers = [];

            markerElements.forEach((card) => {
                const lat = parseFloat(card.dataset.lat || "");
                const lng = parseFloat(card.dataset.lng || "");
                if (Number.isNaN(lat) || Number.isNaN(lng)) {
                    return;
                }

                bounds.extend({ lat, lng });

                const marker = new AdvancedMarkerElement({
                    position: { lat, lng },
                    map,
                    content: createRealizationMarkerContent(),
                });

                const meta = getMarkerMeta(card);
                marker.addListener("click", () => {
                    const infoEl = buildInfoElement(meta);
                    if (infoEl) {
                        realizationsInfoWindow.setContent(infoEl);
                        realizationsInfoWindow.open({ map, anchor: marker });
                    }
                });

                markers.push(marker);
            });

            if (!bounds.isEmpty()) {
                map.fitBounds(bounds);
                if (map.getZoom() > 14) {
                    map.setZoom(14);
                }
            }

            if (markers.length > 1) {
                new MarkerClusterer({
                    map,
                    markers,
                    minimumClusterSize: 2,
                    renderer: {
                        render: ({ markers: clusterMarkers, position }) => {
                            if (clusterMarkers.length < 2) {
                                return null;
                            }

                            return new AdvancedMarkerElement({
                                position,
                                content: createClusterMarkerElement(clusterMarkers.length),
                                zIndex: clusterMarkers.length,
                            });
                        },
                    },
                });
            }

        })
        .catch((error) => {
            console.error("Error initializing realizations map:", error);
        });
};

const initializeMaps = () => {
    initDealerMap();
    initRealizationsMap();
};

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeMaps);
} else {
    initializeMaps();
}

// Listen for HTMX afterSettle events to reinitialize maps after Sprig swaps
document.body.addEventListener("htmx:afterSettle", (event) => {
    // Only reinitialize if the settled element contains or is a realizations component
    if (event.target.querySelector?.("[data-realizations-map]") || event.target.matches?.("[data-realizations-component]")) {
        initRealizationsMap();
    }
});

window.initializeMaps = initializeMaps;
